import React, { Fragment, useState, useEffect } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCalculator, faFileInvoiceDollar, faExclamationTriangle, faPaperclip, faFloppyDisk, faFileExcel, faFilePdf } from "@fortawesome/pro-regular-svg-icons";
import { faLoader, faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form, Field } from "formik";

import ErrorBoundary from "../../containers/ErrorBoundary";
import NotificationToast from "../Helpers/NotificationToast";
import SettingsTextInput from "../Forms/SettingsTextInput";
import SubmitButton from "../Forms/SubmitButton";
import { getBooking } from "../../store/booking/bookingActions";

export default function ModalAffordabilityCalculator({ current_booking, current_staff, onSave }) {
    const dispatch = useDispatch();
    const [open] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [bankStatement, setBankStatement] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    
    // New state for tabs and document types
    const [activeTab, setActiveTab] = useState('main'); // 'main', 'co_applicant', 'company', 'company_main', 'company_co'
    const [activeDocType, setActiveDocType] = useState('bank_statement'); // 'bank_statement', 'payslip', 'tpn', 'all'
    const [filesByApplicant, setFilesByApplicant] = useState({ main: [], co_applicant: [], company: [] });
    const [filesByApplicantCompany, setFilesByApplicantCompany] = useState({ main_only: [], co_applicant_only: [], company_only: [] });
    const [tpnReports, setTpnReports] = useState({ main: [], co_applicant: [], company: [] });
    const [bookingInfo, setBookingInfo] = useState({ joint_application: false, company_application: false });
    const [tpnSummary, setTpnSummary] = useState({ main: '', co_applicant: '', company: '' });
    const [showSelfEmployedCalc, setShowSelfEmployedCalc] = useState(false);
    const [monthlyIncomes, setMonthlyIncomes] = useState(['', '', '', '', '', '']);
    const [allFiles, setAllFiles] = useState([]);
    const [showAllFilesModal, setShowAllFilesModal] = useState(false);
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);
    const [editingFileName, setEditingFileName] = useState('');
    const [renamingFile, setRenamingFile] = useState(false);
    const [householdExpanded, setHouseholdExpanded] = useState(false);
    const formikRef = React.useRef(null);
    
    // Separate state for each applicant type
    const [mainApplicantValues, setMainApplicantValues] = useState({
        basic_salary: "", other_income: "", gross_income: "", nett_salary: "",
        bank_deductions: "", credit_bureau_deductions: "", utilities: "",
        groceries: "", insurance: "", car_repayment: "", loan_repayment: "",
        fuel_costs: "", credit_card_repayments: "", medical: "", other_expenses: "",
        school_fees: "", cellphone_costs: "", rental: "",
        gross_salary: "", fixed_expenses: "", other: "", available_for_rent: "",
        net_income: "", rent: "", car_repayments: "", loan_repayments: "",
        card_repayments: "", groceries_costs: "", medical_aid: "",
        total_monthly: "", available_income: "",
    });
    
    const [coApplicantValues, setCoApplicantValues] = useState({
        basic_salary: "", other_income: "", gross_income: "", nett_salary: "",
        bank_deductions: "", credit_bureau_deductions: "", utilities: "",
        groceries: "", insurance: "", car_repayment: "", loan_repayment: "",
        fuel_costs: "", credit_card_repayments: "", medical: "", other_expenses: "",
        school_fees: "", cellphone_costs: "", rental: "",
        gross_salary: "", fixed_expenses: "", other: "", available_for_rent: "",
        net_income: "", rent: "", car_repayments: "", loan_repayments: "",
        card_repayments: "", groceries_costs: "", medical_aid: "",
        total_monthly: "", available_income: "",
    });
    
    const [companyValues, setCompanyValues] = useState({
        basic_salary: "", other_income: "", gross_income: "", nett_salary: "",
        bank_deductions: "", credit_bureau_deductions: "", utilities: "",
        groceries: "", insurance: "", car_repayment: "", loan_repayment: "",
        fuel_costs: "", credit_card_repayments: "", medical: "", other_expenses: "",
        school_fees: "", cellphone_costs: "", rental: "",
        gross_salary: "", fixed_expenses: "", other: "", available_for_rent: "",
        net_income: "", rent: "", car_repayments: "", loan_repayments: "",
        card_repayments: "", groceries_costs: "", medical_aid: "",
        total_monthly: "", available_income: "",
    });
    
    // Current values based on active tab
    const [initialValues, setInitialValues] = useState({
        // Income fields
        basic_salary: "",
        other_income: "",
        gross_income: "",
        nett_salary: "",
        
        // Bank Deductions
        bank_deductions: "",
        
        // Credit Bureau Deductions
        credit_bureau_deductions: "",
        
        // Standard Utility Costs
        utilities: "",
        
        // Household Expenditure
        groceries: "",
        insurance: "",
        car_repayment: "",
        loan_repayment: "",
        fuel_costs: "",
        credit_card_repayments: "",
        medical: "",
        other_expenses: "",
        school_fees: "",
        cellphone_costs: "",
        
        // Rental
        rental: "",
        
        // Legacy fields - kept for backward compatibility
        gross_salary: "",
        fixed_expenses: "",
        other: "",
        available_for_rent: "",
        net_income: "",
        rent: "",
        car_repayments: "",
        loan_repayments: "",
        card_repayments: "",
        groceries_costs: "",
        medical_aid: "",
        total_monthly: "",
        available_income: "",
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch user data with affordability info
            const userResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}booking_user_id/${current_booking.user_id}/${current_booking.id}`
            );
            const data = userResponse.data;

            // Set initial values from affordability data for main applicant
            if (data.user?.affordability) {
                const aff = data.user.affordability;
                const mainValues = {
                    basic_salary: aff.basic_salary || "",
                    other_income: aff.other_income || "",
                    gross_income: aff.gross_income || "",
                    nett_salary: aff.nett_salary || aff.gross_salary || "",
                    bank_deductions: aff.bank_deductions || "",
                    credit_bureau_deductions: aff.credit_bureau_deductions || "",
                    utilities: aff.utilities || "",
                    groceries: aff.groceries || aff.groceries_costs || "",
                    insurance: aff.insurance || "",
                    car_repayment: aff.car_repayment || aff.car_repayments || "",
                    loan_repayment: aff.loan_repayment || aff.loan_repayments || "",
                    fuel_costs: aff.fuel_costs || "",
                    credit_card_repayments: aff.credit_card_repayments || aff.card_repayments || "",
                    medical: aff.medical || aff.medical_aid || "",
                    other_expenses: aff.other_expenses || aff.other || "",
                    school_fees: aff.school_fees || "",
                    cellphone_costs: aff.cellphone_costs || "",
                    rental: aff.rental || aff.rent || "",
                    gross_salary: aff.gross_salary || "",
                    fixed_expenses: aff.fixed_expenses || "",
                    other: aff.other || "",
                    available_for_rent: aff.available_for_rent || "",
                    net_income: aff.net_income || "",
                    rent: aff.rent || "",
                    car_repayments: aff.car_repayments || "",
                    loan_repayments: aff.loan_repayments || "",
                    card_repayments: aff.card_repayments || "",
                    groceries_costs: aff.groceries_costs || "",
                    medical_aid: aff.medical_aid || "",
                    total_monthly: aff.total_monthly || "",
                    available_income: aff.available_income || "",
                };
                setMainApplicantValues(mainValues);
                setInitialValues(mainValues);
            }
            
            // Set co-applicant affordability data
            if (data.user?.joint_application_affordability) {
                const coAff = data.user.joint_application_affordability;
                setCoApplicantValues({
                    basic_salary: coAff.basic_salary || "",
                    other_income: coAff.other_income || "",
                    gross_income: coAff.gross_income || "",
                    nett_salary: coAff.nett_salary || coAff.gross_salary || "",
                    bank_deductions: coAff.bank_deductions || "",
                    credit_bureau_deductions: coAff.credit_bureau_deductions || "",
                    utilities: coAff.utilities || "",
                    groceries: coAff.groceries || coAff.groceries_costs || "",
                    insurance: coAff.insurance || "",
                    car_repayment: coAff.car_repayment || coAff.car_repayments || "",
                    loan_repayment: coAff.loan_repayment || coAff.loan_repayments || "",
                    fuel_costs: coAff.fuel_costs || "",
                    credit_card_repayments: coAff.credit_card_repayments || coAff.card_repayments || "",
                    medical: coAff.medical || coAff.medical_aid || "",
                    other_expenses: coAff.other_expenses || coAff.other || "",
                    school_fees: coAff.school_fees || "",
                    cellphone_costs: coAff.cellphone_costs || "",
                    rental: coAff.rental || coAff.rent || "",
                    gross_salary: coAff.gross_salary || "",
                    fixed_expenses: coAff.fixed_expenses || "",
                    other: coAff.other || "",
                    available_for_rent: coAff.available_for_rent || "",
                    net_income: coAff.net_income || "",
                    rent: coAff.rent || "",
                    car_repayments: coAff.car_repayments || "",
                    loan_repayments: coAff.loan_repayments || "",
                    card_repayments: coAff.card_repayments || "",
                    groceries_costs: coAff.groceries_costs || "",
                    medical_aid: coAff.medical_aid || "",
                    total_monthly: coAff.total_monthly || "",
                    available_income: coAff.available_income || "",
                });
            }
            
            // Set company affordability data
            if (data.user?.company_affordability) {
                const compAff = data.user.company_affordability;
                setCompanyValues({
                    basic_salary: compAff.basic_salary || "",
                    other_income: compAff.other_income || "",
                    gross_income: compAff.gross_income || "",
                    nett_salary: compAff.nett_salary || compAff.gross_salary || "",
                    bank_deductions: compAff.bank_deductions || "",
                    credit_bureau_deductions: compAff.credit_bureau_deductions || "",
                    utilities: compAff.utilities || "",
                    groceries: compAff.groceries || compAff.groceries_costs || "",
                    insurance: compAff.insurance || "",
                    car_repayment: compAff.car_repayment || compAff.car_repayments || "",
                    loan_repayment: compAff.loan_repayment || compAff.loan_repayments || "",
                    fuel_costs: compAff.fuel_costs || "",
                    credit_card_repayments: compAff.credit_card_repayments || compAff.card_repayments || "",
                    medical: compAff.medical || compAff.medical_aid || "",
                    other_expenses: compAff.other_expenses || compAff.other || "",
                    school_fees: compAff.school_fees || "",
                    cellphone_costs: compAff.cellphone_costs || "",
                    rental: compAff.rental || compAff.rent || "",
                    gross_salary: compAff.gross_salary || "",
                    fixed_expenses: compAff.fixed_expenses || "",
                    other: compAff.other || "",
                    available_for_rent: compAff.available_for_rent || "",
                    net_income: compAff.net_income || "",
                    rent: compAff.rent || "",
                    car_repayments: compAff.car_repayments || "",
                    loan_repayments: compAff.loan_repayments || "",
                    card_repayments: compAff.card_repayments || "",
                    groceries_costs: compAff.groceries_costs || "",
                    medical_aid: compAff.medical_aid || "",
                    total_monthly: compAff.total_monthly || "",
                    available_income: compAff.available_income || "",
                });
            }

            // Fetch files with new structure
            const filesResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}booking/${current_booking.id}/files`
            );
            
            console.log('Files API Response:', filesResponse.data);
            
            // Store files by applicant type
            const filesByApplicantData = filesResponse.data.files_by_applicant || { main: [], co_applicant: [], company: [] };
            const filesByApplicantCompanyData = filesResponse.data.files_by_applicant_company || { main_only: [], co_applicant_only: [], company_only: [] };
            const tpnReportsData = filesResponse.data.tpn_reports || { main: [], co_applicant: [], company: [] };
            
            console.log('Files by applicant:', filesByApplicantData);
            console.log('Files by applicant+company:', filesByApplicantCompanyData);
            setFilesByApplicant(filesByApplicantData);
            setFilesByApplicantCompany(filesByApplicantCompanyData);
            
            // Store TPN reports and load request_notes
            console.log('TPN reports:', tpnReportsData);
            setTpnReports(tpnReportsData);
            
            // Get unit info from API response
            const unitInfo = filesResponse.data.unit_info || {};
            console.log('🏢 Unit info from API:', unitInfo);
            
            // Load TPN request_notes into tpnSummary state
            const tpnSummaryData = { main: '', co_applicant: '', company: '' };
            
            // Build template with building name and unit number from API response
            const userName = current_booking?.user?.name || '';
            const userSurname = current_booking?.user?.surname || '';
            const fullName = `${userName} ${userSurname}`.trim();
            const buildingName = unitInfo.building_name || '';
            const unitNumber = unitInfo.unit_number || '';
            const unitType = unitInfo.unit_type_name || '';
            const leaseInfo = unitType ? `${unitType} & lease for ${new Date().toLocaleString('default', { month: 'short' })}` : '';
            const submittedBy = current_staff?.name && current_staff?.surname 
                ? `${current_staff.name} ${current_staff.surname}` 
                : '';
            
            console.log('📝 Template values:', { fullName, buildingName, unitNumber, unitType, leaseInfo, submittedBy });
            
            const template = `${fullName}
${leaseInfo}
Building: ${buildingName}
Unit: ${unitType}
 - 
 - TPN
 - Credit Payments
 – Defaults
Works at  – to be verified
Net left: 
Submitted by: ${submittedBy}`;
            
            // Initialize all applicant types, not just those with TPN records
            ['main', 'co_applicant', 'company'].forEach(applicantType => {
                const tpns = tpnReportsData[applicantType];
                console.log(`🔍 Checking ${applicantType} TPN:`, tpns);
                if (tpns && tpns.length > 0) {
                    // Use existing notes if they exist, otherwise use template
                    const existingNotes = tpns[0].request_notes;
                    console.log(`📄 ${applicantType} existing notes:`, existingNotes);
                    tpnSummaryData[applicantType] = existingNotes || template;
                } else {
                    // No TPN record yet, use template
                    tpnSummaryData[applicantType] = template;
                }
            });
            
            console.log('✅ Final tpnSummaryData:', tpnSummaryData);
            setTpnSummary(tpnSummaryData);
            
            // Store booking info for tab visibility
            if (filesResponse.data.booking_info) {
                console.log('Booking info:', filesResponse.data.booking_info);
                setBookingInfo(filesResponse.data.booking_info);
            }
            
            // Store all files for "All Files" modal
            const combinedFiles = [
                ...(filesResponse.data.files || []),
                ...(filesResponse.data.untagged_files || [])
            ];
            setAllFiles(combinedFiles);
            console.log('Combined files:', combinedFiles.length);

            // Load initial preview - use the data directly, not state
            // Only load if we're still on bank_statement (default), don't override user selection
            if (activeDocType === 'bank_statement') {
                console.log('🚀 Loading initial bank statement preview...');
                loadInitialBankStatement(filesByApplicantData.main);
            } else {
                console.log('⏭️ Skipping initial load - user has selected:', activeDocType);
            }
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setPreviewError("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const loadInitialBankStatement = (mainApplicantFiles) => {
        console.log('📄 loadInitialBankStatement - files:', mainApplicantFiles);
        setPreviewLoading(true);
        setPreviewError(null);
        
        // Find approved bank statement
        let targetFile = mainApplicantFiles.find(file => {
            const isApproved = file.status === "Approved" || file.status === "Approve";
            if (!isApproved) return false;
            
            if (file.file_type?.name === "Bank Statement") return true;
            if (typeof file.file_type === "string") {
                const ft = file.file_type.toLowerCase();
                if (ft === "bankstatement" || ft === "bank statement" || ft === "bank_statement") return true;
            }
            return false;
        });
        
        // Fallback to any bank statement if no approved found
        if (!targetFile) {
            targetFile = mainApplicantFiles.find(file => {
                if (file.file_type?.name === "Bank Statement") return true;
                if (typeof file.file_type === "string") {
                    const ft = file.file_type.toLowerCase();
                    if (ft === "bankstatement" || ft === "bank statement" || ft === "bank_statement") return true;
                }
                return false;
            });
            if (targetFile) {
                setPreviewError(`Bank statement found but status is "${targetFile.status || 'Pending'}"`);
            }
        }
        
        if (targetFile) {
            console.log('✅ Found bank statement:', targetFile.name);
            setBankStatement(targetFile);
            loadFilePreview(targetFile.id);
        } else {
            console.log('❌ No bank statement found');
            setBankStatement(null);
            setFilePreview(null);
            setPreviewError("No bank statement found for main applicant");
            setPreviewLoading(false);
        }
    };

    const loadFilePreview = async (fileId) => {
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}preview_file`,
                { fileId: fileId }
            );
            if (response.data && typeof response.data === "object") {
                setFilePreview(response.data);
            } else {
                setPreviewError("Invalid file data received");
            }
        } catch (error) {
            console.error("Error loading file preview:", error);
            setPreviewError("Failed to load file preview");
        } finally {
            setPreviewLoading(false);
        }
    };

    const loadDocumentPreview = (applicantType, docType, fileIndex = 0) => {
        console.log('🔄 loadDocumentPreview v2.0 - applicantType:', applicantType, 'docType:', docType, 'fileIndex:', fileIndex);
        console.log('📁 filesByApplicant:', filesByApplicant);
        console.log('📋 tpnReports:', tpnReports);
        
        setPreviewLoading(true);
        setPreviewError(null);
        setBankStatement(null);
        
        try {
            let targetFile = null;
            const docTypeName = docType.replace('_', ' ');
            
            if (docType === 'bank_statement') {
                // Get all bank statements for this applicant
                const bankStatements = getFilesOfType(applicantType, 'bank_statement');
                
                if (bankStatements.length > 0 && fileIndex < bankStatements.length) {
                    targetFile = bankStatements[fileIndex];
                    
                    // Check if approved
                    const isApproved = targetFile.status === "Approved" || targetFile.status === "Approve";
                    if (!isApproved) {
                        setPreviewError(`Bank statement found but status is "${targetFile.status || 'Pending'}"`);
                    }
                }
            } else if (docType === 'payslip') {
                // Get all payslips for this applicant
                const payslips = getFilesOfType(applicantType, 'payslip');
                
                if (payslips.length > 0 && fileIndex < payslips.length) {
                    targetFile = payslips[fileIndex];
                    
                    // Check if approved
                    const isApproved = targetFile.status === "Approved" || targetFile.status === "Approve";
                    if (!isApproved) {
                        setPreviewError(`Payslip found but status is "${targetFile.status || 'Pending'}"`);
                    }
                }
            } else if (docType === 'tpn') {
                console.log('🔍 Looking for TPN - applicantType:', applicantType);
                // First, look for TPN files in filesByApplicant (these are actual file uploads)
                const applicantFiles = filesByApplicant[applicantType] || [];
                console.log('🔎 Searching for TPN file in applicant files:', applicantFiles.length, 'files');
                
                const tpnFile = applicantFiles.find(file => {
                    const fileTypeName = file.file_type?.name || file.file_type || '';
                    const fileName = file.name || '';
                    // Check both file_type and file name for TPN
                    return fileTypeName.toLowerCase().includes('tpn') || fileName.toLowerCase().includes('tpn');
                });
                
                if (tpnFile) {
                    console.log('✅ Found TPN file in filesByApplicant:', tpnFile.name);
                    setBankStatement(tpnFile);
                    loadFilePreview(tpnFile.id);
                    return;
                }
                
                // If no file found, check TPN records for pdf_url
                console.log('📋 No TPN file found, checking TPN records...');
                const tpns = tpnReports[applicantType] || [];
                console.log('📋 TPN records for applicant:', tpns.length, 'records');
                
                if (tpns.length > 0) {
                    const tpn = tpns[0]; // Get most recent
                    console.log('📄 TPN record found:', tpn.id, 'status:', tpn.status);
                    
                    // Check if TPN has attached files
                    if (tpn.files && tpn.files.length > 0) {
                        console.log('✅ TPN has attached files, loading first file');
                        const tpnAttachedFile = tpn.files[0];
                        setBankStatement(tpnAttachedFile);
                        loadFilePreview(tpnAttachedFile.id);
                        return;
                    }
                    
                    // Fallback to pdf_url if available
                    if (tpn.pdf_url) {
                        console.log('⚠️ Using pdf_url as fallback:', tpn.pdf_url);
                        setFilePreview({ url: tpn.pdf_url });
                        setBankStatement({ name: 'TPN Report', status: tpn.status });
                        setPreviewLoading(false);
                        return;
                    }
                    
                    // TPN exists but no files or URL
                    setBankStatement(null);
                    setFilePreview(null);
                    const applicantName = applicantType === 'main' ? 'main applicant' : applicantType === 'co_applicant' ? 'co-applicant' : 'company';
                    setPreviewError(`TPN report found but no file available for ${applicantName}`);
                    setPreviewLoading(false);
                    return;
                }
                
                // No TPN found at all
                setBankStatement(null);
                setFilePreview(null);
                const applicantName = applicantType === 'main' ? 'main applicant' : applicantType === 'co_applicant' ? 'co-applicant' : 'company';
                setPreviewError(`No TPN report found for ${applicantName}`);
                setPreviewLoading(false);
                return;
            }
            
            if (targetFile) {
                setBankStatement(targetFile);
                loadFilePreview(targetFile.id);
            } else {
                setBankStatement(null);
                setFilePreview(null);
                const applicantName = applicantType === 'main' ? 'main applicant' : applicantType === 'co_applicant' ? 'co-applicant' : 'company';
                setPreviewError(`No ${docTypeName} found for ${applicantName}`);
                setPreviewLoading(false);
            }
        } catch (error) {
            console.error("Error loading document preview:", error);
            setPreviewError("Failed to load document");
            setPreviewLoading(false);
        }
    };

    const handleTabChange = (tab, currentFormValues = null) => {
        // Save current form values before switching tabs and get updated state
        let updatedMainValues = mainApplicantValues;
        let updatedCoValues = coApplicantValues;
        let updatedCompanyValues = companyValues;
        
        if (currentFormValues) {
            if (activeTab === 'main') {
                updatedMainValues = currentFormValues;
                setMainApplicantValues(currentFormValues);
            } else if (activeTab === 'co_applicant') {
                updatedCoValues = currentFormValues;
                setCoApplicantValues(currentFormValues);
            } else if (activeTab === 'company' || activeTab === 'company_main' || activeTab === 'company_co') {
                updatedCompanyValues = currentFormValues;
                setCompanyValues(currentFormValues);
            }
        }
        
        setActiveTab(tab);
        setSelectedFileIndex(0); // Reset to first file when changing applicant
        
        // Update initialValues based on selected tab using the updated values
        if (tab === 'main') {
            setInitialValues(updatedMainValues);
        } else if (tab === 'co_applicant') {
            setInitialValues(updatedCoValues);
        } else if (tab === 'company' || tab === 'company_main' || tab === 'company_co') {
            setInitialValues(updatedCompanyValues);
        }
        
        // Company tab uses main applicant files
        const applicantType = tab === 'company' ? 'main' : tab;
        loadDocumentPreview(applicantType, activeDocType, 0);
    };

    const handleFileNavigation = (index) => {
        setSelectedFileIndex(index);
        const applicantType = activeTab === 'company' ? 'main' : activeTab;
        loadDocumentPreview(applicantType, activeDocType, index);
    };

    const handleRenameFile = async () => {
        if (!bankStatement || !editingFileName || editingFileName === bankStatement.name) {
            return;
        }

        setRenamingFile(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}rename_file/${bankStatement.id}`,
                { name: editingFileName }
            );
            
            toast(
                <NotificationToast
                    title="File Name Updated Successfully"
                    message={`Name changed to ${editingFileName}`}
                />
            );
            
            // Update the file name in local state
            setBankStatement({ ...bankStatement, name: editingFileName });
            
            // Refresh the files list
            fetchData();
        } catch (error) {
            console.error('Error renaming file:', error);
            toast(
                <NotificationToast
                    title="Something went wrong"
                    message="Failed to rename file"
                    type="error"
                />
            );
        } finally {
            setRenamingFile(false);
        }
    };

    // Helper function to check if an applicant has any affordability data
    const hasAffordabilityData = (values) => {
        if (!values) return false;
        
        const fields = [
            'basic_salary', 'other_income', 'nett_salary', 'gross_income',
            'bank_deductions', 'credit_bureau_deductions', 'utilities',
            'groceries', 'insurance', 'car_repayment', 'loan_repayment',
            'fuel_costs', 'credit_card_repayments', 'medical', 'other_expenses',
            'school_fees', 'cellphone_costs', 'rental'
        ];
        
        return fields.some(field => {
            const value = formatToFloat(values[field]);
            return value && value > 0;
        });
    };

    const handleExportToExcel = async (values) => {
        try {
            toast(
                <NotificationToast
                    title="Generating Excel File"
                    message="Please wait..."
                />
            );

            // Gather all applicants' data
            const applicants = [];
            
            // Main applicant
            if (hasAffordabilityData(mainApplicantValues)) {
                applicants.push({
                    type: 'main',
                    label: 'Applicant 1',
                    data: {
                        basic_salary: formatToFloat(mainApplicantValues.basic_salary) || 0,
                        other_income: formatToFloat(mainApplicantValues.other_income) || 0,
                        nett_salary: formatToFloat(mainApplicantValues.nett_salary) || 0,
                        bank_deductions: formatToFloat(mainApplicantValues.bank_deductions) || 0,
                        credit_bureau_deductions: formatToFloat(mainApplicantValues.credit_bureau_deductions) || 0,
                        utilities: formatToFloat(mainApplicantValues.utilities) || 0,
                        groceries: formatToFloat(mainApplicantValues.groceries) || 0,
                        insurance: formatToFloat(mainApplicantValues.insurance) || 0,
                        car_repayment: formatToFloat(mainApplicantValues.car_repayment) || 0,
                        loan_repayment: formatToFloat(mainApplicantValues.loan_repayment) || 0,
                        fuel_costs: formatToFloat(mainApplicantValues.fuel_costs) || 0,
                        credit_card_repayments: formatToFloat(mainApplicantValues.credit_card_repayments) || 0,
                        medical: formatToFloat(mainApplicantValues.medical) || 0,
                        other_expenses: formatToFloat(mainApplicantValues.other_expenses) || 0,
                        school_fees: formatToFloat(mainApplicantValues.school_fees) || 0,
                        cellphone_costs: formatToFloat(mainApplicantValues.cellphone_costs) || 0,
                        rental: formatToFloat(mainApplicantValues.rental) || 0,
                    }
                });
            }
            
            // Co-applicant (only if joint application)
            if (bookingInfo.joint_application && hasAffordabilityData(coApplicantValues)) {
                applicants.push({
                    type: 'co_applicant',
                    label: 'Applicant 2',
                    data: {
                        basic_salary: formatToFloat(coApplicantValues.basic_salary) || 0,
                        other_income: formatToFloat(coApplicantValues.other_income) || 0,
                        nett_salary: formatToFloat(coApplicantValues.nett_salary) || 0,
                        bank_deductions: formatToFloat(coApplicantValues.bank_deductions) || 0,
                        credit_bureau_deductions: formatToFloat(coApplicantValues.credit_bureau_deductions) || 0,
                        utilities: formatToFloat(coApplicantValues.utilities) || 0,
                        groceries: formatToFloat(coApplicantValues.groceries) || 0,
                        insurance: formatToFloat(coApplicantValues.insurance) || 0,
                        car_repayment: formatToFloat(coApplicantValues.car_repayment) || 0,
                        loan_repayment: formatToFloat(coApplicantValues.loan_repayment) || 0,
                        fuel_costs: formatToFloat(coApplicantValues.fuel_costs) || 0,
                        credit_card_repayments: formatToFloat(coApplicantValues.credit_card_repayments) || 0,
                        medical: formatToFloat(coApplicantValues.medical) || 0,
                        other_expenses: formatToFloat(coApplicantValues.other_expenses) || 0,
                        school_fees: formatToFloat(coApplicantValues.school_fees) || 0,
                        cellphone_costs: formatToFloat(coApplicantValues.cellphone_costs) || 0,
                        rental: formatToFloat(coApplicantValues.rental) || 0,
                    }
                });
            }
            
            // Company applicants (only if company application)
            if (bookingInfo.company_application && hasAffordabilityData(companyValues)) {
                // Determine label based on whether it's dual company or single
                const companyLabel = bookingInfo.joint_application ? 'Company 1' : 'Company';
                applicants.push({
                    type: 'company_main',
                    label: companyLabel,
                    data: {
                        basic_salary: formatToFloat(companyValues.basic_salary) || 0,
                        other_income: formatToFloat(companyValues.other_income) || 0,
                        nett_salary: formatToFloat(companyValues.nett_salary) || 0,
                        bank_deductions: formatToFloat(companyValues.bank_deductions) || 0,
                        credit_bureau_deductions: formatToFloat(companyValues.credit_bureau_deductions) || 0,
                        utilities: formatToFloat(companyValues.utilities) || 0,
                        groceries: formatToFloat(companyValues.groceries) || 0,
                        insurance: formatToFloat(companyValues.insurance) || 0,
                        car_repayment: formatToFloat(companyValues.car_repayment) || 0,
                        loan_repayment: formatToFloat(companyValues.loan_repayment) || 0,
                        fuel_costs: formatToFloat(companyValues.fuel_costs) || 0,
                        credit_card_repayments: formatToFloat(companyValues.credit_card_repayments) || 0,
                        medical: formatToFloat(companyValues.medical) || 0,
                        other_expenses: formatToFloat(companyValues.other_expenses) || 0,
                        school_fees: formatToFloat(companyValues.school_fees) || 0,
                        cellphone_costs: formatToFloat(companyValues.cellphone_costs) || 0,
                        rental: formatToFloat(companyValues.rental) || 0,
                    }
                });
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}affordability/export`,
                {
                    booking_id: current_booking.id,
                    applicant_name: `${current_booking?.user?.name || ''} ${current_booking?.user?.surname || ''}`.trim(),
                    property_name: current_booking?.unit?.building?.name || '',
                    applicants: applicants, // Send all applicants data
                },
                { responseType: 'blob' }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Affordability_${current_booking?.user?.name}_${current_booking?.user?.surname}_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // Check if file was saved to booking (file ID in response header)
            const fileId = response.headers['x-file-id'];
            
            toast(
                <NotificationToast
                    title="Excel File Downloaded"
                    message={fileId ? "File downloaded and saved to booking attachments" : "File downloaded successfully"}
                />
            );
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast(
                <NotificationToast
                    title="Export Failed"
                    message="Failed to generate Excel file"
                    type="error"
                />
            );
        }
    };

    const handleExportToPdf = async (values) => {
        try {
            toast(
                <NotificationToast
                    title="Generating PDF File"
                    message="Please wait..."
                />
            );

            // Gather all applicants' data
            const applicants = [];
            
            // Main applicant
            if (hasAffordabilityData(mainApplicantValues)) {
                applicants.push({
                    type: 'main',
                    label: 'Applicant 1',
                    data: {
                        basic_salary: formatToFloat(mainApplicantValues.basic_salary) || 0,
                        other_income: formatToFloat(mainApplicantValues.other_income) || 0,
                        nett_salary: formatToFloat(mainApplicantValues.nett_salary) || 0,
                        bank_deductions: formatToFloat(mainApplicantValues.bank_deductions) || 0,
                        credit_bureau_deductions: formatToFloat(mainApplicantValues.credit_bureau_deductions) || 0,
                        utilities: formatToFloat(mainApplicantValues.utilities) || 0,
                        groceries: formatToFloat(mainApplicantValues.groceries) || 0,
                        insurance: formatToFloat(mainApplicantValues.insurance) || 0,
                        car_repayment: formatToFloat(mainApplicantValues.car_repayment) || 0,
                        loan_repayment: formatToFloat(mainApplicantValues.loan_repayment) || 0,
                        fuel_costs: formatToFloat(mainApplicantValues.fuel_costs) || 0,
                        credit_card_repayments: formatToFloat(mainApplicantValues.credit_card_repayments) || 0,
                        medical: formatToFloat(mainApplicantValues.medical) || 0,
                        other_expenses: formatToFloat(mainApplicantValues.other_expenses) || 0,
                        school_fees: formatToFloat(mainApplicantValues.school_fees) || 0,
                        cellphone_costs: formatToFloat(mainApplicantValues.cellphone_costs) || 0,
                        rental: formatToFloat(mainApplicantValues.rental) || 0,
                    }
                });
            }
            
            // Co-applicant (only if joint application)
            if (bookingInfo.joint_application && hasAffordabilityData(coApplicantValues)) {
                applicants.push({
                    type: 'co_applicant',
                    label: 'Applicant 2',
                    data: {
                        basic_salary: formatToFloat(coApplicantValues.basic_salary) || 0,
                        other_income: formatToFloat(coApplicantValues.other_income) || 0,
                        nett_salary: formatToFloat(coApplicantValues.nett_salary) || 0,
                        bank_deductions: formatToFloat(coApplicantValues.bank_deductions) || 0,
                        credit_bureau_deductions: formatToFloat(coApplicantValues.credit_bureau_deductions) || 0,
                        utilities: formatToFloat(coApplicantValues.utilities) || 0,
                        groceries: formatToFloat(coApplicantValues.groceries) || 0,
                        insurance: formatToFloat(coApplicantValues.insurance) || 0,
                        car_repayment: formatToFloat(coApplicantValues.car_repayment) || 0,
                        loan_repayment: formatToFloat(coApplicantValues.loan_repayment) || 0,
                        fuel_costs: formatToFloat(coApplicantValues.fuel_costs) || 0,
                        credit_card_repayments: formatToFloat(coApplicantValues.credit_card_repayments) || 0,
                        medical: formatToFloat(coApplicantValues.medical) || 0,
                        other_expenses: formatToFloat(coApplicantValues.other_expenses) || 0,
                        school_fees: formatToFloat(coApplicantValues.school_fees) || 0,
                        cellphone_costs: formatToFloat(coApplicantValues.cellphone_costs) || 0,
                        rental: formatToFloat(coApplicantValues.rental) || 0,
                    }
                });
            }
            
            // Company applicants (only if company application)
            if (bookingInfo.company_application && hasAffordabilityData(companyValues)) {
                // Determine label based on whether it's dual company or single
                const companyLabel = bookingInfo.joint_application ? 'Company 1' : 'Company';
                applicants.push({
                    type: 'company_main',
                    label: companyLabel,
                    data: {
                        basic_salary: formatToFloat(companyValues.basic_salary) || 0,
                        other_income: formatToFloat(companyValues.other_income) || 0,
                        nett_salary: formatToFloat(companyValues.nett_salary) || 0,
                        bank_deductions: formatToFloat(companyValues.bank_deductions) || 0,
                        credit_bureau_deductions: formatToFloat(companyValues.credit_bureau_deductions) || 0,
                        utilities: formatToFloat(companyValues.utilities) || 0,
                        groceries: formatToFloat(companyValues.groceries) || 0,
                        insurance: formatToFloat(companyValues.insurance) || 0,
                        car_repayment: formatToFloat(companyValues.car_repayment) || 0,
                        loan_repayment: formatToFloat(companyValues.loan_repayment) || 0,
                        fuel_costs: formatToFloat(companyValues.fuel_costs) || 0,
                        credit_card_repayments: formatToFloat(companyValues.credit_card_repayments) || 0,
                        medical: formatToFloat(companyValues.medical) || 0,
                        other_expenses: formatToFloat(companyValues.other_expenses) || 0,
                        school_fees: formatToFloat(companyValues.school_fees) || 0,
                        cellphone_costs: formatToFloat(companyValues.cellphone_costs) || 0,
                        rental: formatToFloat(companyValues.rental) || 0,
                    }
                });
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}affordability/export-pdf`,
                {
                    booking_id: current_booking.id,
                    applicant_name: `${current_booking?.user?.name || ''} ${current_booking?.user?.surname || ''}`.trim(),
                    property_name: current_booking?.unit?.building?.name || '',
                    unit_type: current_booking?.unit?.unit_type || '',
                    applicants: applicants, // Send all applicants data
                },
                { responseType: 'blob' }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Affordability_${current_booking?.user?.name}_${current_booking?.user?.surname}_${new Date().toISOString().split('T')[0]}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // Check if file was saved to booking (file ID in response header)
            const fileId = response.headers['x-file-id'];
            
            toast(
                <NotificationToast
                    title="PDF File Downloaded"
                    message={fileId ? "File downloaded and saved to booking attachments" : "File downloaded successfully"}
                />
            );
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast(
                <NotificationToast
                    title="Export Failed"
                    message="Failed to generate PDF file"
                    type="error"
                />
            );
        }
    };

    const handleDocTypeChange = (docType) => {
        console.log('📎 handleDocTypeChange called with:', docType);
        setActiveDocType(docType);
        setSelectedFileIndex(0); // Reset to first file when changing doc type
        if (docType === 'all') {
            console.log('📎 Showing all files in preview panel');
            // Show file list in the preview panel instead of opening a modal
            setPreviewLoading(false);
            setPreviewError(null);
            setBankStatement(null);
            setFilePreview(null);
        } else {
            // Company tab uses main applicant files
            const applicantType = activeTab === 'company' ? 'main' : activeTab;
            loadDocumentPreview(applicantType, docType, 0);
        }
    };

    // Helper function to get files for current tab based on complex logic
    const getFilesForCurrentTab = () => {
        const isDualCompany = bookingInfo.joint_application && bookingInfo.company_application;
        
        if (isDualCompany) {
            // Complex scenario: dual company tabs
            switch(activeTab) {
                case 'main':
                    return filesByApplicantCompany.main_only || [];
                case 'co_applicant':
                    return filesByApplicantCompany.co_applicant_only || [];
                case 'company_main':
                    return filesByApplicantCompany.company_main || [];
                case 'company_co':
                    return filesByApplicantCompany.company_co || [];
                default:
                    return [];
            }
        } else {
            // Simple scenario: use regular file grouping
            return filesByApplicant[activeTab] || [];
        }
    };

    // Helper function to get all files of a specific type for current applicant
    const getFilesOfType = (applicantType, docType) => {
        const files = getFilesForCurrentTab();
        const docTypeName = docType.replace('_', ' ');
        
        if (docType === 'bank_statement') {
            return files.filter(file => {
                if (file.file_type?.name === "Bank Statement") return true;
                if (typeof file.file_type === "string") {
                    const ft = file.file_type.toLowerCase();
                    if (ft === "bankstatement" || ft === "bank statement" || ft === "bank_statement") return true;
                }
                return false;
            });
        } else if (docType === 'payslip') {
            return files.filter(file => {
                if (file.file_type?.name === "Payslip") return true;
                if (typeof file.file_type === "string") {
                    const ft = file.file_type.toLowerCase();
                    if (ft === "payslip" || ft === "pay slip") return true;
                }
                return false;
            });
        }
        return [];
    };

    function formatToFloat(value) {
        if (value) {
            return parseFloat(value.toString().replace(/,/g, ""));
        } else {
            return value;
        }
    }

    function availableIncome(income, outcome) {
        const money_in = formatToFloat(income) || 0;
        const money_out = formatToFloat(outcome) || 0;
        return money_in - money_out;
    }

    function calculateAvailableForRent(income, expenses) {
        const gross = formatToFloat(income) || 0;
        const totalExpenses = formatToFloat(expenses) || 0;
        return gross - totalExpenses;
    }

    function calculateHouseholdExpenses(values) {
        // Calculate household expenses from individual fields
        const householdExpenses = [
            formatToFloat(values.groceries),
            formatToFloat(values.insurance),
            formatToFloat(values.car_repayment),
            formatToFloat(values.loan_repayment),
            formatToFloat(values.fuel_costs),
            formatToFloat(values.credit_card_repayments),
            formatToFloat(values.medical),
            formatToFloat(values.other_expenses),
            formatToFloat(values.school_fees),
            formatToFloat(values.cellphone_costs),
        ].reduce((sum, expense) => sum + (expense || 0), 0);
        
        return householdExpenses;
    }

    function calculateTotalExpenses(values, setFieldValue) {
        // Calculate based on new structure
        const bankDeductions = formatToFloat(values.bank_deductions) || 0;
        const creditBureauDeductions = formatToFloat(values.credit_bureau_deductions) || 0;
        const utilities = formatToFloat(values.utilities) || 0;
        const householdExpenses = calculateHouseholdExpenses(values);
        
        const total = bankDeductions + creditBureauDeductions + utilities + householdExpenses;
        return total;
    }

    function calculateLegacyTotalExpenses(values) {
        const expenses = [
            formatToFloat(values.rent),
            formatToFloat(values.car_repayments),
            formatToFloat(values.loan_repayments),
            formatToFloat(values.card_repayments),
            formatToFloat(values.groceries_costs),
            formatToFloat(values.fuel_costs),
            formatToFloat(values.school_fees),
            formatToFloat(values.medical_aid),
        ];
        
        return expenses.reduce((sum, expense) => sum + (expense || 0), 0);
    }

    const handleSave = async (values, { setSubmitting, resetForm }) => {
        setSaving(true);
        setSubmitting(true);

        const url = current_booking?.edit_link?.edit_url || current_booking?.user?.edit_url;
        
        // Update the appropriate state based on active tab
        if (activeTab === 'main') {
            setMainApplicantValues(values);
        } else if (activeTab === 'co_applicant') {
            setCoApplicantValues(values);
        } else if (activeTab === 'company' || activeTab === 'company_main' || activeTab === 'company_co') {
            setCompanyValues(values);
        }

        const submitData = {
            user: current_staff,
            url: url,
            applicant_type: activeTab, // Add applicant type to submission
            // New structure fields
            basic_salary: formatToFloat(values.basic_salary),
            other_income: formatToFloat(values.other_income),
            gross_income: formatToFloat(values.gross_income),
            nett_salary: formatToFloat(values.nett_salary),
            bank_deductions: formatToFloat(values.bank_deductions),
            credit_bureau_deductions: formatToFloat(values.credit_bureau_deductions),
            utilities: formatToFloat(values.utilities),
            groceries: formatToFloat(values.groceries),
            insurance: formatToFloat(values.insurance),
            car_repayment: formatToFloat(values.car_repayment),
            loan_repayment: formatToFloat(values.loan_repayment),
            fuel_costs: formatToFloat(values.fuel_costs),
            credit_card_repayments: formatToFloat(values.credit_card_repayments),
            medical: formatToFloat(values.medical),
            other_expenses: formatToFloat(values.other_expenses),
            school_fees: formatToFloat(values.school_fees),
            cellphone_costs: formatToFloat(values.cellphone_costs),
            rental: formatToFloat(values.rental),
            available_for_rent: calculateAvailableForRent(
                values.nett_salary,
                calculateTotalExpenses(values)
            ),
            // Legacy fields - still saved for backward compatibility
            gross_salary: formatToFloat(values.nett_salary) || formatToFloat(values.gross_salary),
            fixed_expenses: formatToFloat(values.fixed_expenses),
            other: formatToFloat(values.other_expenses) || values.other,
            net_income: formatToFloat(values.net_income),
            rent: formatToFloat(values.rent),
            car_repayments: formatToFloat(values.car_repayment) || formatToFloat(values.car_repayments),
            loan_repayments: formatToFloat(values.loan_repayment) || formatToFloat(values.loan_repayments),
            card_repayments: formatToFloat(values.credit_card_repayments) || formatToFloat(values.card_repayments),
            groceries_costs: formatToFloat(values.groceries) || formatToFloat(values.groceries_costs),
            medical_aid: formatToFloat(values.medical) || formatToFloat(values.medical_aid),
            total_monthly: formatToFloat(values.total_monthly),
            available_income: availableIncome(values.nett_salary || values.net_income, values.total_monthly),
        };

        try {
            await axios.put(`${process.env.REACT_APP_API_URL}user`, submitData);
            
            // Save TPN Summary (request_notes) for each applicant type - using update-notes endpoint
            console.log('💾 Saving TPN Summary...', tpnSummary);
            const tpnSavePromises = [];
            Object.keys(tpnSummary).forEach(applicantType => {
                const tpns = tpnReports[applicantType];
                if (tpns && tpns.length > 0 && tpnSummary[applicantType] !== undefined) {
                    const tpnId = tpns[0].id;
                    console.log(`📝 Saving TPN notes for ${applicantType}, TPN ID: ${tpnId}`);
                    console.log(`📄 Notes content:`, tpnSummary[applicantType]);
                    
                    tpnSavePromises.push(
                        axios.put(`${process.env.REACT_APP_API_URL}tpn-approval/${tpnId}/update-notes`, {
                            request_notes: tpnSummary[applicantType]
                        }).then(response => {
                            console.log(`✅ TPN notes saved for ${applicantType}:`, response.data);
                            return response;
                        }).catch(error => {
                            console.error(`❌ Failed to save TPN notes for ${applicantType}:`, error);
                            throw error;
                        })
                    );
                }
            });
            
            if (tpnSavePromises.length > 0) {
                console.log(`⏳ Waiting for ${tpnSavePromises.length} TPN save request(s)...`);
                await Promise.all(tpnSavePromises);
                console.log('✅ All TPN notes saved successfully');
            } else {
                console.log('ℹ️ No TPN notes to save');
            }
            
            toast(
                <NotificationToast
                    title="Affordability Updated"
                    message="Your changes have been saved."
                />
            );
            
            // Close modal but don't refresh full page
            if (onSave) onSave();
            dispatch(closeModal());
        } catch (error) {
            console.error(error);
            toast(
                <NotificationToast
                    title="Error"
                    message="Failed to save affordability data"
                    type="error"
                />
            );
        } finally {
            setSaving(false);
            setSubmitting(false);
        }
    };

    return (
        <>
            <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                static
                className="fixed z-50 inset-0 overflow-y-auto"
                open={open}
                onClose={() => dispatch(closeModal())}
            >
                <ErrorBoundary>
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                            &#8203;
                        </span>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-[95vw] h-[90vh]">
                                {/* Header */}
                                <div className="h-14 flex rounded-t-lg items-center justify-between bg-dark_background px-6">
                                    <div className="flex items-center">
                                        <FontAwesomeIcon icon={faCalculator} className="h-5 w-5 text-white mr-3" />
                                        <h1 className="text-xl leading-6 font-light text-white capitalize">
                                            Affordability Calculator
                                        </h1>
                                    </div>
                                    <button
                                        type="button"
                                        className="text-white hover:text-gray-300 focus:outline-none"
                                        onClick={() => dispatch(closeModal())}
                                    >
                                        <FontAwesomeIcon className="h-6 w-6" aria-hidden="true" icon={faTimes} />
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="flex h-[calc(90vh-56px)]">
                                    {/* Left Panel - Document Preview */}
                                    <div className="w-1/2 border-r border-gray-200 flex flex-col">
                                        <div className="border-b border-gray-200 bg-gray-50">
                                            <div className="p-4 pb-2">
                                                <h2 className="text-lg font-medium text-gray-900 flex items-center mb-3">
                                                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2 text-primary" />
                                                    Affordability Calculator
                                                </h2>
                                                
                                                {/* Applicant Tabs */}
                                                <div className="flex space-x-2 mb-3">
                                                    {/* Show different tabs based on application type */}
                                                    {bookingInfo.joint_application && bookingInfo.company_application ? (
                                                        // Dual company tabs scenario - show separate company tabs for each applicant
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    const currentValues = formikRef.current?.values;
                                                                    handleTabChange('main', currentValues);
                                                                }}
                                                                className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                                                                    activeTab === 'main'
                                                                        ? 'bg-white text-primary border-t-2 border-primary'
                                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                            >
                                                                Applicant 1
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    const currentValues = formikRef.current?.values;
                                                                    handleTabChange('co_applicant', currentValues);
                                                                }}
                                                                className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                                                                    activeTab === 'co_applicant'
                                                                        ? 'bg-white text-primary border-t-2 border-primary'
                                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                            >
                                                                Applicant 2
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    const currentValues = formikRef.current?.values;
                                                                    handleTabChange('company_main', currentValues);
                                                                }}
                                                                className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                                                                    activeTab === 'company_main'
                                                                        ? 'bg-white text-purple-600 border-t-2 border-purple-600'
                                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                            >
                                                                Company 1
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    const currentValues = formikRef.current?.values;
                                                                    handleTabChange('company_co', currentValues);
                                                                }}
                                                                className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                                                                    activeTab === 'company_co'
                                                                        ? 'bg-white text-purple-600 border-t-2 border-purple-600'
                                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                            >
                                                                Company 2
                                                            </button>
                                                        </>
                                                    ) : (
                                                        // Simple scenario
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    const currentValues = formikRef.current?.values;
                                                                    handleTabChange('main', currentValues);
                                                                }}
                                                                className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                                                                    activeTab === 'main'
                                                                        ? 'bg-white text-primary border-t-2 border-primary'
                                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                            >
                                                                Main Applicant
                                                            </button>
                                                            {bookingInfo.joint_application && (
                                                                <button
                                                                    onClick={() => {
                                                                        const currentValues = formikRef.current?.values;
                                                                        handleTabChange('co_applicant', currentValues);
                                                                    }}
                                                                    className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                                                                        activeTab === 'co_applicant'
                                                                            ? 'bg-white text-primary border-t-2 border-primary'
                                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                    }`}
                                                                >
                                                                    Co-Applicant
                                                                </button>
                                                            )}
                                                            {bookingInfo.company_application && (
                                                                <button
                                                                    onClick={() => {
                                                                        const currentValues = formikRef.current?.values;
                                                                        handleTabChange('company', currentValues);
                                                                    }}
                                                                    className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                                                                        activeTab === 'company'
                                                                            ? 'bg-white text-purple-600 border-t-2 border-purple-600'
                                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                    }`}
                                                                >
                                                                    Company
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                
                                                {/* Document Type Buttons */}
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleDocTypeChange('bank_statement')}
                                                        className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                                                            activeDocType === 'bank_statement'
                                                                ? 'bg-primary text-white'
                                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        Bank Statement
                                                    </button>
                                                    {activeTab !== 'company' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleDocTypeChange('payslip')}
                                                                className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                                                                    activeDocType === 'payslip'
                                                                        ? 'bg-primary text-white'
                                                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                Payslip
                                                            </button>
                                                            <button
                                                                onClick={() => handleDocTypeChange('tpn')}
                                                                className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                                                                    activeDocType === 'tpn'
                                                                        ? 'bg-primary text-white'
                                                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                TPN Report
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        onClick={() => handleDocTypeChange('all')}
                                                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                                                            activeDocType === 'all'
                                                                ? 'bg-primary text-white border border-primary'
                                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                        title="View all files"
                                                    >
                                                        <FontAwesomeIcon icon={faPaperclip} />
                                                    </button>
                                                </div>
                                                
                                                {/* File Navigation Tabs - Only show for bank_statement and payslip */}
                                                {(activeDocType === 'bank_statement' || activeDocType === 'payslip') && (() => {
                                                    const applicantType = activeTab === 'company' ? 'main' : activeTab;
                                                    const filesOfType = getFilesOfType(applicantType, activeDocType);
                                                    
                                                    return filesOfType.length > 1 ? (
                                                        <div className="flex space-x-1 overflow-x-auto pb-2 pt-2 border-t border-gray-200 mt-2">
                                                            {filesOfType.map((file, index) => {
                                                                const fileName = file.name || `File ${index + 1}`;
                                                                const truncatedName = fileName.length > 25 ? fileName.substring(0, 25) + '...' : fileName;
                                                                return (
                                                                    <button
                                                                        key={file.id}
                                                                        onClick={() => handleFileNavigation(index)}
                                                                        className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                                                                            selectedFileIndex === index
                                                                                ? 'bg-primary text-white'
                                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                                        }`}
                                                                        title={fileName}
                                                                    >
                                                                        {truncatedName}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : null;
                                                })()}
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-auto p-4 flex flex-col">
                                            {loading || previewLoading ? (
                                                <div className="flex flex-col items-center justify-center h-full">
                                                    <FontAwesomeIcon 
                                                        icon={faLoader} 
                                                        className="h-8 w-8 text-primary animate-spin mb-4" 
                                                    />
                                                    <p className="text-gray-500">Loading preview...</p>
                                                </div>
                                            ) : activeDocType === 'all' ? (
                                                <div className="flex-1 overflow-auto p-4">
                                                    <h3 className="text-lg font-medium mb-4">All Files for {activeTab === 'main' ? 'Main Applicant' : activeTab === 'co_applicant' ? 'Co-Applicant' : 'Company'}</h3>
                                                    {(() => {
                                                        const filesToShow = activeTab === 'company' ? filesByApplicant['main'] : filesByApplicant[activeTab];
                                                        return filesToShow?.length > 0 ? (
                                                            <ul className="divide-y divide-gray-200">
                                                                {filesToShow.map((file) => (
                                                                    <li
                                                                        key={file.id}
                                                                        className="py-3 flex items-center justify-between hover:bg-gray-50 px-2 rounded cursor-pointer"
                                                                        onClick={() => {
                                                                            console.log('🖱️ File clicked:', file.name, file.id);
                                                                            setBankStatement(file);
                                                                            loadFilePreview(file.id);
                                                                            setActiveDocType('bank_statement');
                                                                        }}
                                                                    >
                                                                        <div className="flex-1">
                                                                            <p className="text-sm font-medium text-gray-900">
                                                                                {file.file_type?.name || file.file_type || 'Unknown Type'}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500">
                                                                                {file.name}.{file.extension}
                                                                            </p>
                                                                        </div>
                                                                        <span className={`px-2 py-1 text-xs rounded ${
                                                                            file.status === 'Approved' || file.status === 'Approve'
                                                                                ? 'bg-green-100 text-green-800'
                                                                                : file.status === 'Declined'
                                                                                ? 'bg-red-100 text-red-800'
                                                                                : 'bg-yellow-100 text-yellow-800'
                                                                        }`}>
                                                                            {file.status || 'Pending'}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p className="text-sm text-gray-500 text-center py-4">
                                                                No files found for this applicant
                                                            </p>
                                                        );
                                                    })()}
                                                </div>
                                            ) : !bankStatement && !filePreview && activeDocType === 'bank_statement' ? (
                                                <div className="flex flex-col items-center justify-center h-full">
                                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md text-center">
                                                        <FontAwesomeIcon 
                                                            icon={faExclamationTriangle} 
                                                            className="h-12 w-12 text-yellow-500 mb-4" 
                                                        />
                                                        <h3 className="text-lg font-medium text-yellow-800 mb-2">
                                                            No Bank Statement Found
                                                        </h3>
                                                        <p className="text-sm text-yellow-700">
                                                            {previewError || "There is no bank statement available for this booking. You can still manually fill in the affordability form on the right."}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : !bankStatement && !filePreview ? (
                                                <div className="flex flex-col items-center justify-center h-full">
                                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-md text-center">
                                                        <FontAwesomeIcon 
                                                            icon={faExclamationTriangle} 
                                                            className="h-12 w-12 text-gray-400 mb-4" 
                                                        />
                                                        <h3 className="text-lg font-medium text-gray-700 mb-2">
                                                            No Document Found
                                                        </h3>
                                                        <p className="text-sm text-gray-600">
                                                            {previewError || "No document available for this selection."}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* File name editor */}
                                                    {bankStatement && (
                                                        <div className="mb-3 flex items-center space-x-2">
                                                            <input
                                                                type="text"
                                                                value={editingFileName || bankStatement.name}
                                                                onChange={(e) => setEditingFileName(e.target.value)}
                                                                onFocus={() => {
                                                                    if (!editingFileName) {
                                                                        setEditingFileName(bankStatement.name);
                                                                    }
                                                                }}
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                                                placeholder="File name"
                                                                disabled={renamingFile}
                                                            />
                                                            {editingFileName && editingFileName !== bankStatement.name && (
                                                                <button
                                                                    onClick={handleRenameFile}
                                                                    disabled={renamingFile}
                                                                    className="px-3 py-2 bg-primary text-white rounded-md hover:bg-primary_hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                                                    title="Save Name"
                                                                >
                                                                    <FontAwesomeIcon icon={faFloppyDisk} className="h-4 w-4" />
                                                                    <span className="text-sm">Save</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    {/* Show warning banner if not approved */}
                                                    {previewError && (
                                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3 flex items-center">
                                                            <FontAwesomeIcon 
                                                                icon={faExclamationTriangle} 
                                                                className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0" 
                                                            />
                                                            <p className="text-sm text-yellow-700">{previewError}</p>
                                                        </div>
                                                    )}
                                                    {/* File preview */}
                                                    {filePreview?.url ? (
                                                        <iframe
                                                            src={filePreview.url}
                                                            title="Document Preview"
                                                            className="w-full flex-1 border rounded"
                                                        />
                                                    ) : filePreview?.path ? (
                                                        <img
                                                            src={`${process.env.REACT_APP_R2_DEVURL}/public/${filePreview.path}`}
                                                            alt="Bank Statement"
                                                            className="max-w-full h-auto rounded"
                                                        />
                                                    ) : (
                                                        <div className="flex flex-col items-center justify-center flex-1">
                                                            <p className="text-gray-500">Unable to display preview</p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Panel - Affordability Form */}
                                    <div className="w-1/2 flex flex-col">
                                        <div className="p-4 border-b border-gray-200 bg-gray-50">
                                            <h2 className="text-lg font-medium text-gray-900 flex items-center">
                                                <FontAwesomeIcon icon={faCalculator} className="mr-2 text-primary" />
                                                Affordability Details
                                            </h2>
                                            <p className="text-sm text-gray-500 mt-1">
                                                For {current_booking?.user?.name} {current_booking?.user?.surname}
                                            </p>
                                        </div>
                                        <div className="flex-1 overflow-auto p-6">
                                            {loading ? (
                                                <div className="flex flex-col items-center justify-center h-full">
                                                    <FontAwesomeIcon 
                                                        icon={faLoader} 
                                                        className="h-8 w-8 text-primary animate-spin mb-4" 
                                                    />
                                                    <p className="text-gray-500">Loading...</p>
                                                </div>
                                            ) : (
                                                <Formik
                                                    key={activeTab}
                                                    innerRef={formikRef}
                                                    initialValues={initialValues}
                                                    enableReinitialize={true}
                                                    onSubmit={handleSave}
                                                >
                                                    {({ values, setFieldValue, handleSubmit, isSubmitting, isValid, dirty }) => (
                                                        <Form onSubmit={handleSubmit} className="space-y-4">
                                                            {/* TPN Summary Section - only show if pending TPN exists */}
                                                            {(() => {
                                                                const applicantType = activeTab === 'company' ? 'main' : activeTab;
                                                                const tpns = tpnReports[applicantType] || [];
                                                                const hasPendingTpn = tpns.some(tpn => tpn.status === 'pending' || tpn.status === 'in_progress');
                                                                return hasPendingTpn;
                                                            })() && (
                                                            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                                                <h3 className="text-md font-semibold text-purple-900 mb-3">TPN Summary</h3>
                                                                <textarea
                                                                    value={tpnSummary[activeTab === 'company' ? 'main' : activeTab] || ''}
                                                                    onChange={(e) => {
                                                                        const applicantType = activeTab === 'company' ? 'main' : activeTab;
                                                                        setTpnSummary({
                                                                            ...tpnSummary,
                                                                            [applicantType]: e.target.value
                                                                        });
                                                                    }}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                                                    rows="4"
                                                                    placeholder="Enter TPN summary notes here..."
                                                                />
                                                            </div>
                                                            )}

                                                            {/* Income Section */}
                                                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h3 className="text-md font-semibold text-blue-900">Income</h3>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowSelfEmployedCalc(!showSelfEmployedCalc)}
                                                                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                                                    >
                                                                        {showSelfEmployedCalc ? 'Hide' : 'Self-Employed Calculator'}
                                                                    </button>
                                                                </div>
                                                                
                                                                {showSelfEmployedCalc && (
                                                                    <div className="mb-4 p-3 bg-white rounded border border-blue-300">
                                                                        <h4 className="text-sm font-semibold text-blue-800 mb-2">6 Month Income Average</h4>
                                                                        <div className="grid grid-cols-3 gap-2 mb-3">
                                                                            {monthlyIncomes.map((income, index) => (
                                                                                <div key={index}>
                                                                                    <label className="text-xs text-gray-600">Month {index + 1}</label>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={income}
                                                                                        onChange={(e) => {
                                                                                            const newIncomes = [...monthlyIncomes];
                                                                                            newIncomes[index] = e.target.value;
                                                                                            setMonthlyIncomes(newIncomes);
                                                                                        }}
                                                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                                                                        placeholder="R 0.00"
                                                                                    />
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                const total = monthlyIncomes.reduce((sum, val) => {
                                                                                    const num = parseFloat(val.replace(/[^0-9.-]/g, '')) || 0;
                                                                                    return sum + num;
                                                                                }, 0);
                                                                                const average = total / 6;
                                                                                const nettSalary = average / 2;
                                                                                setFieldValue('nett_salary', nettSalary.toFixed(2));
                                                                            }}
                                                                            className="w-full px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                                                                        >
                                                                            Calculate & Set Nett Salary
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <Field 
                                                                        name="basic_salary" 
                                                                        as={SettingsTextInput} 
                                                                        label="Basic Salary" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="other_income" 
                                                                        as={SettingsTextInput} 
                                                                        label="Other Income" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="nett_salary" 
                                                                        as={SettingsTextInput} 
                                                                        label="Basic Nett Salary" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Bank Deductions Section */}
                                                            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                                                <h3 className="text-md font-semibold text-purple-900 mb-3">Bank Deductions</h3>
                                                                <p className="text-xs text-purple-700 mb-3">Subscriptions, debit orders, etc.</p>
                                                                <div className="grid grid-cols-1 gap-4">
                                                                    <Field 
                                                                        name="bank_deductions" 
                                                                        as={SettingsTextInput} 
                                                                        label="Total Bank Deductions" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Credit Bureau Deductions Section */}
                                                            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                                                <h3 className="text-md font-semibold text-red-900 mb-3">Credit Bureau - Defaults / Judgements</h3>
                                                                <div className="grid grid-cols-1 gap-4">
                                                                    <Field 
                                                                        name="credit_bureau_deductions" 
                                                                        as={SettingsTextInput} 
                                                                        label="Total Credit Bureau Deductions" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Standard Utility Costs Section */}
                                                            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                                                <h3 className="text-md font-semibold text-yellow-900 mb-3">Standard Utility Costs</h3>
                                                                <div className="grid grid-cols-1 gap-4">
                                                                    <Field 
                                                                        name="utilities" 
                                                                        as={SettingsTextInput} 
                                                                        label="Utilities" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Household Expenditure Section */}
                                                            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                                                                <div className="flex justify-between items-center mb-3">
                                                                    <div>
                                                                        <h3 className="text-md font-semibold text-orange-900">Household Expenditure</h3>
                                                                        <p className="text-xs text-orange-700">Minimum R2000.00</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-4">
                                                                        <div className="text-right">
                                                                            <div className="text-xs text-orange-700">Total</div>
                                                                            <div className="text-lg font-bold text-orange-900">
                                                                                R {calculateHouseholdExpenses(values).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setHouseholdExpanded(!householdExpanded)}
                                                                            className="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center gap-2"
                                                                        >
                                                                            <FontAwesomeIcon icon={householdExpanded ? faChevronUp : faChevronDown} />
                                                                            {householdExpanded ? 'Collapse' : 'Expand'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                {householdExpanded && (
                                                                    <div className="grid grid-cols-2 gap-4 mt-4">
                                                                    <Field 
                                                                        name="groceries" 
                                                                        as={SettingsTextInput} 
                                                                        label="Groceries & Toiletries" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="insurance" 
                                                                        as={SettingsTextInput} 
                                                                        label="Insurance" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="car_repayment" 
                                                                        as={SettingsTextInput} 
                                                                        label="Car Repayment" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="loan_repayment" 
                                                                        as={SettingsTextInput} 
                                                                        label="Loan Repayment" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="fuel_costs" 
                                                                        as={SettingsTextInput} 
                                                                        label="Fuel/Travel Costs" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="credit_card_repayments" 
                                                                        as={SettingsTextInput} 
                                                                        label="Credit Card Repayments" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="medical" 
                                                                        as={SettingsTextInput} 
                                                                        label="Medical" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="other_expenses" 
                                                                        as={SettingsTextInput} 
                                                                        label="Other" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="school_fees" 
                                                                        as={SettingsTextInput} 
                                                                        label="School" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    <Field 
                                                                        name="cellphone_costs" 
                                                                        as={SettingsTextInput} 
                                                                        label="Cellphone Costs" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Rental Section */}
                                                            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                                                                <h3 className="text-md font-semibold text-indigo-900 mb-3">Rental</h3>
                                                                <div className="grid grid-cols-1 gap-4">
                                                                    <Field 
                                                                        name="rental" 
                                                                        as={SettingsTextInput} 
                                                                        label="Rental Amount" 
                                                                        prepend="R" 
                                                                        currency="true" 
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Available for Rent - Calculated */}
                                                            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-md font-semibold text-green-900">
                                                                        Available for Rent
                                                                    </span>
                                                                    <span className={`text-xl font-bold ${
                                                                        calculateAvailableForRent(values.nett_salary, calculateTotalExpenses(values)) >= 0 
                                                                            ? 'text-green-600' 
                                                                            : 'text-red-600'
                                                                    }`}>
                                                                        R {calculateAvailableForRent(values.nett_salary, calculateTotalExpenses(values)).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Total Monthly Expenses - Display Only */}
                                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-sm font-medium text-gray-700">
                                                                        Total Monthly Expenses (Display Only)
                                                                    </span>
                                                                    <span className="text-lg font-semibold text-gray-900">
                                                                        R {calculateTotalExpenses(values).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="pt-4 flex justify-between items-center">
                                                                <div className="flex space-x-3">
                                                                    <button
                                                                        type="button"
                                                                        className="inline-flex items-center justify-center rounded-md border border-green-600 shadow-sm px-4 py-2 bg-green-600 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                                        onClick={() => handleExportToExcel(values)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                                                                        Export to Excel
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="inline-flex items-center justify-center rounded-md border border-red-600 shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                                        onClick={() => handleExportToPdf(values)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                                                                        Export to PDF
                                                                    </button>
                                                                </div>
                                                                <div className="flex space-x-3">
                                                                    <button
                                                                        type="button"
                                                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                                        onClick={() => dispatch(closeModal())}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                    <SubmitButton
                                                                        name="Save"
                                                                        disabled={isSubmitting}
                                                                        isSubmitting={isSubmitting || saving}
                                                                        text="Save Affordability"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Form>
                                                    )}
                                                </Formik>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </ErrorBoundary>
            </Dialog>
            </Transition.Root>
        </>
    );
}
